import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Briefcase, Save, Euro, Clock, Users, Camera, 
  Plus, X, Loader2, Check, Award, Landmark, CreditCard, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categories that support per-person pricing
const PER_PERSON_CATEGORIES = ['chef', 'fitness', 'beauty'];

const RANK_COLORS = {
  pro: "#4A90D9",
  elite: "#9B59B6",
  master: "#F1C40F",
  grandmaster: "#E74C3C",
  legendary: "#E07A5F"
};

export default function ProviderProfile() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  
  // Form state
  const [bio, setBio] = useState('');
  const [hourlyRate, setHourlyRate] = useState(30);
  const [minHours, setMinHours] = useState(1);
  const [maxCapacity, setMaxCapacity] = useState(10);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(['de', 'en']);
  const [isAvailable, setIsAvailable] = useState(true);
  const [profileImage, setProfileImage] = useState('');
  
  // Pricing type state
  const [pricingType, setPricingType] = useState('hourly');
  const [perPersonRate, setPerPersonRate] = useState(25);
  const [supportsPerPerson, setSupportsPerPerson] = useState(false);
  
  // Stripe status
  const [stripeStatus, setStripeStatus] = useState(null);
  
  // Bank info state
  const [bankAccountHolder, setBankAccountHolder] = useState('');
  const [bankIban, setBankIban] = useState('');
  const [bankBic, setBankBic] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankSaving, setBankSaving] = useState(false);

  const allLanguages = ['de', 'en', 'fr', 'it', 'es'];

  useEffect(() => {
    if (user?.role !== 'provider') {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const dashRes = await fetch(`${API_URL}/api/dashboard/provider`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });

        if (dashRes.ok) {
          const data = await dashRes.json();
          const provider = data.provider_profile;
          setProfile(provider);
          setBio(provider.bio || '');
          setHourlyRate(provider.hourly_rate);
          setMinHours(provider.min_hours);
          setMaxCapacity(provider.max_capacity);
          setSelectedSpecialties(provider.specialties || []);
          setSelectedLanguages(provider.languages || ['de', 'en']);
          setIsAvailable(provider.is_available);
          setProfileImage(provider.profile_image || '');
          
          // Set pricing type
          setPricingType(provider.pricing_type || 'hourly');
          setPerPersonRate(provider.per_person_rate || 25);
          setSupportsPerPerson(PER_PERSON_CATEGORIES.includes(provider.service_category));
          
          // Fetch Stripe status
          const stripeRes = await fetch(`${API_URL}/api/providers/stripe/status`, {
            headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
          });
          if (stripeRes.ok) {
            const stripeData = await stripeRes.json();
            setStripeStatus(stripeData);
          }
          
          // Fetch specialties for category
          const specRes = await fetch(`${API_URL}/api/config/specialties/${provider.service_category}`);
          if (specRes.ok) {
            const specData = await specRes.json();
            setSpecialtiesList(specData.specialties);
          }
          
          // Fetch bank info
          const bankRes = await fetch(`${API_URL}/api/providers/bank-info`, {
            headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
          });
          if (bankRes.ok) {
            const bankData = await bankRes.json();
            setBankAccountHolder(bankData.account_holder || '');
            setBankIban(bankData.iban || '');
            setBankBic(bankData.bic || '');
            setBankName(bankData.bank_name || '');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, user, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/providers/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          bio,
          pricing_type: pricingType,
          hourly_rate: hourlyRate,
          per_person_rate: perPersonRate,
          min_hours: minHours,
          max_capacity: maxCapacity,
          specialties: selectedSpecialties,
          languages: selectedLanguages,
          is_available: isAvailable,
          profile_image: profileImage,
        }),
      });

      if (response.ok) {
        toast.success(t('common.success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialty = (specialty) => {
    setSelectedSpecialties(prev =>
      prev.includes(specialty)
        ? prev.filter(s => s !== specialty)
        : [...prev, specialty]
    );
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages(prev =>
      prev.includes(lang)
        ? prev.filter(l => l !== lang)
        : [...prev, lang]
    );
  };

  const getRankName = (rank) => t(`ranks.${rank}`) || rank;
  const getCategoryName = (category) => t(`categories.${category}`) || category;

  const handleSaveBank = async () => {
    setBankSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/providers/bank-info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          account_holder: bankAccountHolder,
          iban: bankIban,
          bic: bankBic,
          bank_name: bankName,
        }),
      });
      if (response.ok) {
        toast.success(t('bank.saved'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch {
      toast.error(t('common.error'));
    } finally {
      setBankSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-profile-page">
      <div className="container-app max-w-4xl">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-heading text-3xl font-semibold mb-2">
              {t('nav.profile')}
            </h1>
            <p className="text-muted-foreground">
              {getCategoryName(profile?.service_category)}
            </p>
          </div>
          <Button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('common.save')}
              </>
            )}
          </Button>
        </div>

        <div className="space-y-8">
          {/* Rank Display */}
          {profile && (
            <Card className="dashboard-card overflow-hidden">
              <div 
                className="h-2"
                style={{ backgroundColor: RANK_COLORS[profile.rank] }}
              />
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Award className="w-10 h-10" style={{ color: RANK_COLORS[profile.rank] }} />
                  <div>
                    <p className="font-heading text-xl font-semibold">
                      {getRankName(profile.rank)}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {profile.completed_missions} {t('provider.missions')} • {t('provider.commission')}: {(profile.commission_rate * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Availability Toggle */}
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">{t('provider.available')}</h3>
                  <p className="text-sm text-muted-foreground">
                    Toggle to receive bookings
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={isAvailable ? 'text-success' : 'text-muted-foreground'}>
                    {isAvailable ? t('provider.available') : t('provider.unavailable')}
                  </span>
                  <Switch
                    checked={isAvailable}
                    onCheckedChange={setIsAvailable}
                    data-testid="availability-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Image */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Profile Photo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-2xl bg-muted overflow-hidden">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Briefcase className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Label className="mb-2 block">Image URL</Label>
                  <Input
                    placeholder="https://example.com/photo.jpg"
                    value={profileImage}
                    onChange={(e) => setProfileImage(e.target.value)}
                    data-testid="profile-image-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bio */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading">{t('provider.about')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Describe your experience and services..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="min-h-[150px]"
                data-testid="bio-input"
              />
            </CardContent>
          </Card>

          {/* Stripe Connect Status */}
          <Card className="dashboard-card border-2 border-accent/30">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-accent" />
                {t('admin.stripe.setup.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stripeStatus?.charges_enabled ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-700 dark:text-green-400">{t('provider.stripeActive')}</p>
                      <p className="text-sm text-muted-foreground">{t('provider.canReceivePayments')}</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => navigate('/provider/stripe-setup')}>
                    {t('common.manage')}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-400">{t('provider.configRequired')}</p>
                      <p className="text-sm text-muted-foreground">{t('provider.createStripeToReceive')}</p>
                    </div>
                  </div>
                  <Button className="btn-primary" onClick={() => navigate('/provider/stripe-setup')} data-testid="setup-stripe-btn">
                    {t('provider.configureStripe')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Euro className="w-5 h-5" />
                {t('provider.pricing')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pricing Type Selection (only for supported categories) */}
              {supportsPerPerson && (
                <div className="p-4 bg-accent/5 rounded-lg">
                  <Label className="mb-3 block font-medium">{t('provider.billingMode')}</Label>
                  <Select value={pricingType} onValueChange={setPricingType}>
                    <SelectTrigger className="w-full sm:w-64" data-testid="pricing-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {t('provider.hourlyRate')}
                        </div>
                      </SelectItem>
                      <SelectItem value="per_person">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4" />
                          {t('provider.perPersonRate')}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    {pricingType === 'hourly' 
                      ? t('provider.hourlyDescription')
                      : t('provider.perPersonDescription')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Hourly Rate */}
                <div className={pricingType === 'per_person' && supportsPerPerson ? 'opacity-50' : ''}>
                  <Label className="mb-2 block">{t('provider.perHour')} (€)</Label>
                  <Input
                    type="number"
                    min={10}
                    max={200}
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    disabled={pricingType === 'per_person' && supportsPerPerson}
                    data-testid="hourly-rate-input"
                  />
                </div>

                {/* Per Person Rate (only if supported) */}
                {supportsPerPerson && (
                  <div className={pricingType === 'hourly' ? 'opacity-50' : ''}>
                    <Label className="mb-2 block flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      {t('provider.perPerson')} (€)
                    </Label>
                    <Input
                      type="number"
                      min={5}
                      max={200}
                      value={perPersonRate}
                      onChange={(e) => setPerPersonRate(Number(e.target.value))}
                      disabled={pricingType === 'hourly'}
                      data-testid="per-person-rate-input"
                    />
                  </div>
                )}

                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('provider.minHours')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={8}
                    value={minHours}
                    onChange={(e) => setMinHours(Number(e.target.value))}
                    data-testid="min-hours-input"
                  />
                </div>
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t('provider.maxCapacity')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={maxCapacity}
                    onChange={(e) => setMaxCapacity(Number(e.target.value))}
                    data-testid="max-capacity-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Specialties */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading">{t('provider.specialties')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {specialtiesList.map((specialty) => (
                  <Badge
                    key={specialty}
                    variant={selectedSpecialties.includes(specialty) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      selectedSpecialties.includes(specialty) 
                        ? 'bg-accent text-accent-foreground' 
                        : 'hover:bg-accent/10'
                    }`}
                    onClick={() => toggleSpecialty(specialty)}
                    data-testid={`specialty-${specialty}`}
                  >
                    {selectedSpecialties.includes(specialty) && <Check className="w-3 h-3 mr-1" />}
                    {specialty}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Languages */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading">{t('provider.languages')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allLanguages.map((lang) => (
                  <Badge
                    key={lang}
                    variant={selectedLanguages.includes(lang) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      selectedLanguages.includes(lang) 
                        ? 'bg-accent text-accent-foreground' 
                        : 'hover:bg-accent/10'
                    }`}
                    onClick={() => toggleLanguage(lang)}
                    data-testid={`language-${lang}`}
                  >
                    {selectedLanguages.includes(lang) && <Check className="w-3 h-3 mr-1" />}
                    {lang.toUpperCase()}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bank Information */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Landmark className="w-5 h-5" />
                {t('bank.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground mb-2">{t('bank.description')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('bank.accountHolder')}</Label>
                  <Input
                    value={bankAccountHolder}
                    onChange={(e) => setBankAccountHolder(e.target.value)}
                    placeholder="Max Mustermann"
                    data-testid="bank-holder-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bank.bankName')}</Label>
                  <Input
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Deutsche Bank"
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    data-testid="bank-iban-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC / SWIFT</Label>
                  <Input
                    value={bankBic}
                    onChange={(e) => setBankBic(e.target.value)}
                    placeholder="COBADEFFXXX"
                    data-testid="bank-bic-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveBank}
                disabled={bankSaving}
                className="btn-primary"
                data-testid="save-bank-btn"
              >
                {bankSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {bankSaving ? t('common.loading') : t('bank.save')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
